import React from "react";
import { Link } from "react-router-dom";
import { SITE_DISPLAY_NAME } from "../../utility/siteCopy";
import { isDTS } from "../../utility/platform";
import { getSweepsCoinName, getGoldCoinName } from "../../utility/complianceCopy";
import NoPurchaseBanner from "../common-components/NoPurchaseBanner";

function FeatureCard({ title, description, tag }) {
  return (
    <div className="info-feature-card">
      {tag ? <span className="info-feature-card__tag">{tag}</span> : null}
      <h3 className="info-feature-card__title">{title}</h3>
      <p className="info-feature-card__text">{description}</p>
    </div>
  );
}

function PromoCard({ badge, title, description, terms }) {
  return (
    <div className="info-promo-card">
      <span className="info-promo-card__badge">{badge}</span>
      <h3 className="info-promo-card__title">{title}</h3>
      <p className="info-promo-card__text">{description}</p>
      {terms ? <p className="info-promo-card__terms">{terms}</p> : null}
    </div>
  );
}

function StepItem({ number, title, description }) {
  return (
    <div className="info-step">
      <div className="info-step__number">{number}</div>
      <div>
        <h3 className="info-step__title">{title}</h3>
        <p className="info-step__text">{description}</p>
      </div>
    </div>
  );
}

export function DefaultFeaturesContent() {
  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        {SITE_DISPLAY_NAME} gives you a full sportsbook in one dashboard — fast
        lines, clear bet tracking, and tools built for everyday players.
      </p>
      <div className="info-feature-grid">
        <FeatureCard
          tag="Popular"
          title="Straight, Parlay & Teaser"
          description="Build straight bets or combine legs in parlays and teasers. Switch bet types from the builder without leaving the page."
        />
        <FeatureCard
          title="Live odds & match board"
          description="See spread, money line, and totals update on the main board. Expand active bets to view the live match snapshot."
        />
        <FeatureCard
          title="Active Bets hub"
          description="Track open straight, parlay, and teaser tickets in one place with status, risk/win, and quick detail view."
        />
        <FeatureCard
          tag="New"
          title="Cash out"
          description="On eligible straight bets, settle early when a live cash out offer is available — funds return to your wallet instantly."
        />
        <FeatureCard
          title="Recents & favorites"
          description="Jump back to matches you played recently or save favorites for quick access from the betting sidebar."
        />
        <FeatureCard
          title="Secure wallet"
          description="Deposit, track balance and at-risk amounts from the header. Full wager history keeps every ticket on record."
        />
      </div>
    </div>
  );
}

export function DefaultPromoContent() {
  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        Promotions below are sample offers for demo purposes. Your team can
        replace them anytime from the admin Info Pages section.
      </p>
      <div className="info-promo-grid">
        <PromoCard
          badge="Welcome"
          title="First deposit boost"
          description="New players receive a matched bonus on their first wallet top-up. Perfect for exploring straight and parlay betting."
          terms="Example terms: 5× rollover on bonus amount. Admin can edit full rules in CMS."
        />
        <PromoCard
          badge="Weekly"
          title="Parlay profit boost"
          description="Add an extra leg to your parlay and unlock enhanced payout on winning tickets placed during the promo window."
          terms="Min 3 legs · min odds per leg apply · see admin for live dates."
        />
        <PromoCard
          badge="Live"
          title="Same-game specials"
          description="Selected matchups include boosted spreads and totals during live play. Watch the center banner for featured events."
          terms="Availability varies by sport and league."
        />
      </div>
    </div>
  );
}

export function DefaultHowToContent() {
  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        New to {SITE_DISPLAY_NAME}? Follow these steps to place your first wager
        in under a minute.
      </p>
      <div className="info-steps">
        <StepItem
          number="1"
          title="Pick a sport"
          description="Use the left sidebar or A-Z Betting to browse leagues. Filter by sport tabs under the banner carousel."
        />
        <StepItem
          number="2"
          title="Choose a market"
          description="Tap Spread, Money Line, or Total on any match. Your selection lands in the bet slip on the right."
        />
        <StepItem
          number="3"
          title="Enter stake & review"
          description="Type your risk amount. The slip shows potential win and combined odds for parlays or teasers."
        />
        <StepItem
          number="4"
          title="Place bet & track"
          description="Confirm the ticket, then follow it under Open Bets. Cash out when offered on eligible straight wagers."
        />
      </div>
      <div className="info-bet-types">
        <h2 className="info-section-title">Bet types at a glance</h2>
        <ul className="info-bet-types__list">
          <li>
            <strong>Straight</strong> — one pick; simplest way to start.
          </li>
          <li>
            <strong>Parlay</strong> — multiple picks; all must win for payout.
          </li>
          <li>
            <strong>Teaser</strong> — adjusted lines across two or more games.
          </li>
        </ul>
      </div>
    </div>
  );
}

export function DefaultSportsbookContent() {
  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        Our sportsbook covers major US and international leagues with a clean
        dashboard designed for speed and clarity.
      </p>
      <div className="info-stats-row">
        <div className="info-stat">
          <span className="info-stat__value">100+</span>
          <span className="info-stat__label">Leagues & events</span>
        </div>
        <div className="info-stat">
          <span className="info-stat__value">Live</span>
          <span className="info-stat__label">In-play lines</span>
        </div>
        <div className="info-stat">
          <span className="info-stat__value">24/7</span>
          <span className="info-stat__label">Betting access</span>
        </div>
      </div>
      <div className="info-highlight-list">
        <h2 className="info-section-title">What you can do</h2>
        <ul>
          <li>Browse by sport, league tabs, and live Mobius feed</li>
          <li>Build straight, parlay, teaser, and call bet tickets</li>
          <li>Monitor grading status and settlement in Active Bets</li>
          <li>Review filled history and wallet transactions anytime</li>
        </ul>
      </div>
      <p className="info-default-note">
        Questions? Visit our <Link to="/faq">FAQ</Link> or open a support ticket
        from your account menu.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* DTS sweepstakes legal pages (placeholder content).                  */
/* Body copy below is PLACEHOLDER — counsel's final wording replaces   */
/* it via CMS with zero code change. These defaults guarantee the      */
/* pages are never blank, which the sweepstakes law requires day one.  */
/* ------------------------------------------------------------------ */

function LegalPlaceholderNote() {
  return (
    <p className="info-default-note">
      This is placeholder text pending legal review. Final wording will be
      provided by {SITE_DISPLAY_NAME} legal counsel and published here
      without any code change.
    </p>
  );
}

export function DefaultTermsContent() {
  return (
    <div className="info-default-layout">
      <NoPurchaseBanner variant="inline" />
      <p className="info-default-lead">
        These Terms of Use govern your account, use of the {SITE_DISPLAY_NAME}{" "}
        platform and software, platform data, and your responsibilities as a
        user. By creating an account you agree to these terms.
      </p>
      <ul>
        <li>Eligibility, account registration, and permitted use.</li>
        <li>
          {getGoldCoinName()} are a no-cash-value utility item; {getSweepsCoinName()} are a
          free promotional entry credit. {getGoldCoinName()} are never redeemable.
        </li>
        <li>User conduct, platform data and software usage, and liabilities.</li>
        <li>Excluded jurisdictions and the void-where-prohibited rule.</li>
      </ul>
      <LegalPlaceholderNote />
    </div>
  );
}

export function DefaultOfficialRulesContent() {
  return (
    <div className="info-default-layout">
      <NoPurchaseBanner variant="inline" />
      <p className="info-default-lead">
        Official Sweepstakes Rules explaining the dual-token architecture and the
        legal boundaries of promotional play on {SITE_DISPLAY_NAME}.
      </p>
      <ul>
        <li>
          <strong>{getGoldCoinName()} (utility):</strong> purchased or awarded for free
          entertainment; no monetary value and never redeemable.
        </li>
        <li>
          <strong>{getSweepsCoinName()} (promotional):</strong> never sold —
          received only as a free bonus with a {getGoldCoinName()} purchase or via the
          free mail-in Alternative Method of Entry (AMOE). Winnings may be
          redeemable.
        </li>
        <li>Eligibility, excluded states, and how winners are determined.</li>
        <li>
          Alternative Method of Entry — see the dedicated AMOE page for the
          free mail-in method.
        </li>
      </ul>
      <LegalPlaceholderNote />
    </div>
  );
}

export function DefaultPrivacyContent() {
  if (isDTS()) {
    return (
      <div className="info-default-layout">
        <p className="info-default-lead">
          {SITE_DISPLAY_NAME} collects account information needed to create an
          account and check eligibility for participation.
        </p>
        <ul>
          <li>
            We collect your date of birth at registration, along with account
            details such as your name, email address, and state of residence.
            The date of birth you enter is stored in your DTS account.
          </li>
          <li>
            Identity verification can take place through Persona. You provide
            identity documents to the verification provider through its hosted
            flow; DTS does not store copies of those documents or the date of
            birth read from them.
          </li>
          <li>
            DTS stores the verification status and age-check result needed to
            determine eligibility, along with limited provider reference and
            decision information. An administrator may review or override a
            verification decision; the manual process does not collect identity
            documents in DTS.
          </li>
        </ul>
        <LegalPlaceholderNote />
      </div>
    );
  }

  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        This Privacy Policy discloses how {SITE_DISPLAY_NAME} collects, stores,
        and protects player data and identity-verification (KYC) records.
      </p>
      <ul>
        <li>What information we collect and why.</li>
        <li>How KYC identity records are stored and protected.</li>
        <li>Data sharing, retention, and your privacy choices.</li>
      </ul>
      <LegalPlaceholderNote />
    </div>
  );
}

export function DefaultAmoeContent() {
  return (
    <div className="info-default-layout">
      <p className="info-default-lead">
        Alternative Method of Entry (AMOE): you can receive {getSweepsCoinName()}
        {" "}100% free by mail — no purchase necessary.
      </p>
      <p className="info-default-cta">
        <a
          href="/amoe"
          className="img_grd_btn img_btn_fw"
          style={{ display: "inline-block" }}
        >
          Request your code
        </a>
      </p>
      <LegalPlaceholderNote />
    </div>
  );
}

const DEFAULT_PAGES = {
  features: {
    title: "Features",
    Content: DefaultFeaturesContent,
  },
  promo: {
    title: "Promotions",
    Content: DefaultPromoContent,
  },
  "how-to": {
    title: "How to Bet",
    Content: DefaultHowToContent,
  },
  sportsbook: {
    title: "Sportsbook",
    Content: DefaultSportsbookContent,
  },
  terms: {
    title: "Terms of Use",
    Content: DefaultTermsContent,
  },
  "official-rules": {
    title: "Official Sweepstakes Rules",
    Content: DefaultOfficialRulesContent,
  },
  privacy: {
    title: "Privacy Policy",
    Content: DefaultPrivacyContent,
  },
  amoe: {
    title: "Alternative Method of Entry",
    Content: DefaultAmoeContent,
  },
};

/**
 * Legacy PPH-flavored default pages describe a sportsbook ("bet", "wager",
 * "cash out", "deposit"). They must never render on the DTS sweepstakes
 * surface, where they'd be a compliance violation. In DTS mode only the
 * compliant legal set is exposed; unknown slugs fall through to InfoPage's
 * existing generic "not published yet" empty state.
 */
const DTS_ALLOWED_DEFAULT_SLUGS = new Set([
  "terms",
  "official-rules",
  "privacy",
  "amoe",
]);

export function getDefaultInfoPage(slug) {
  const key = String(slug || "").trim().toLowerCase();
  if (isDTS() && !DTS_ALLOWED_DEFAULT_SLUGS.has(key)) {
    return null;
  }
  return DEFAULT_PAGES[key] || null;
}
